// Mapa por tema — a tela onde o curador vê o que está julgando.
//
// Três baldes lado a lado (publicado / validado / aguardando) porque é essa
// comparação que sustenta a decisão: o que já está no ar, o que aprovei e
// ainda não promovi, e o que acabou de chegar.
//
// **Leaflet é dependência empacotada, não CDN.** A PoC carregava de
// `unpkg.com` — dependência de CDN em runtime quebra on-premise (SPEC §13), e
// quem instala na própria infraestrutura costuma ser exatamente quem não tem
// saída para a internet.
//
// O mapa base continua vindo de tile server externo, e isso é diferente: sem
// rede o mapa fica cinza mas a aplicação funciona; sem o Leaflet do CDN a tela
// não carrega.
//
// Os provedores de mapa base, e o porquê de cada um, estão em `basemaps.ts`.

import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { obter, type FeicaoMapa, type Mapa as DadosMapa, type Tema } from "../api.js";
import { Carregando, Falha, Vazio } from "../pecas/basicos.js";
import {
  acharBasemap,
  BASEMAPS,
  lerBasemapSalvo,
  salvarBasemap,
  type Basemap,
} from "../pecas/basemaps.js";
import { desenharFeicao, useMapaLeaflet } from "../pecas/mapaLeaflet.js";

// Literais, e não `var()`: o Leaflet desenha em canvas e não lê variável CSS.
// Precisam acompanhar `--pub`, `--val` e `--agd` do tema escuro.
const CORES: Record<string, string> = {
  publicado: "#3FD68C",
  validado: "#7FA9FF",
  aguardando_validacao: "#E3A93B",
};

// A montagem do Leaflet e a troca do mapa base vivem em `pecas/mapaLeaflet.ts`,
// compartilhadas com o mini-mapa da gaveta. A lista de mapas base, e o porquê de
// cada um, está em `pecas/basemaps.ts`.

const BALDES = ["publicado", "validado", "aguardando_validacao"] as const;
type Balde = (typeof BALDES)[number];

const ROTULO: Record<Balde, string> = {
  publicado: "Publicado",
  validado: "Validado",
  aguardando_validacao: "Aguardando validação",
};

// `aparencia`, não `tema`: nesta tela `tema` já significa tema de DADO
// (sigef, car, energia). Dois sentidos para a mesma palavra no mesmo arquivo é
// como se lê a variável errada.
export function Mapa({ aparencia = "escuro" }: { aparencia?: string }): ReactNode {
  const temas = useQuery({ queryKey: ["temas"], queryFn: () => obter<Tema[]>("/temas") });
  const comCamadas = (temas.data ?? []).filter((t) => t.camadas > 0);
  const [tema, setTema] = useState<string | null>(null);
  const escolhido = tema ?? comCamadas[0]?.id ?? null;

  const [visiveis, setVisiveis] = useState<Record<Balde, boolean>>({
    publicado: true,
    validado: true,
    aguardando_validacao: true,
  });
  const [uf, setUf] = useState("");
  const [limite, setLimite] = useState(1500);
  const [soAreaVisivel, setSoAreaVisivel] = useState(false);
  const [bbox, setBbox] = useState<string | null>(null);
  // Inicializador preguiçoso: `lerBasemapSalvo` toca o `localStorage`, e a
  // forma sem função o chamaria a cada render.
  const [basemapId, setBasemapId] = useState(() => lerBasemapSalvo());
  const basemap = acharBasemap(basemapId);

  const consulta = new URLSearchParams({ limite: String(limite) });
  if (uf) consulta.set("uf", uf);
  if (soAreaVisivel && bbox) consulta.set("bbox", bbox);

  const mapa = useQuery({
    queryKey: ["mapa", escolhido, consulta.toString()],
    queryFn: () => obter<DadosMapa>(`/mapa/${escolhido}?${consulta.toString()}`),
    enabled: escolhido !== null,
  });

  if (temas.isLoading) return <Carregando />;
  if (comCamadas.length === 0) return <Vazio>Nenhum tema com camadas.</Vazio>;

  return (
    <>
      <div className="filtros">
        {comCamadas.map((t) => (
          <button
            key={t.id}
            className="chip"
            aria-pressed={escolhido === t.id}
            onClick={() => setTema(t.id)}
          >
            {t.nome}
          </button>
        ))}
      </div>

      <div className="mapa-casca">
        {/* As classes de tema moram AQUI, e não no nó em que o Leaflet monta.
            Quando o `className` de um nó muda, o React reescreve o atributo
            `class` inteiro — e o Leaflet põe as classes dele (`leaflet-container`,
            `leaflet-grab`, `leaflet-touch`…) imperativamente, fora do
            conhecimento do React. Com `tema-escuro` no próprio contêiner do
            mapa, trocar de tema APAGAVA todas elas: o mapa perdia
            `overflow: hidden`, o cursor de arrastar e o fundo, sem erro nenhum
            no console. Medido em `tema.test.tsx`. Trocar de mapa base muda
            `inverte-base`, e o risco é o mesmo — por isso ela mora aqui também.

            `inverte-base` decide se o filtro do tema escuro alcança os tiles.
            Imagem aérea invertida vira negativo — vegetação rosa, água branca —,
            e isso não é tema escuro, é ilegível.

            Este div é do React do começo ao fim, e o de dentro tem classe
            constante. */}
        <div
          className={[
            "mapa-area",
            aparencia === "escuro" ? "tema-escuro" : "",
            basemap.inverteNoEscuro ? "inverte-base" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Tela
            dados={mapa.data ?? null}
            visiveis={visiveis}
            basemap={basemap}
            aoMover={(b) => setBbox(b)}
          />
        </div>

        <div className="mapa-legenda">
          {mapa.error ? <Falha erro={mapa.error} /> : null}

          <div className="legenda-titulo">Status de curadoria</div>
          {BALDES.map((b) => (
            <button
              key={b}
              className="balde"
              aria-pressed={visiveis[b]}
              onClick={() => setVisiveis({ ...visiveis, [b]: !visiveis[b] })}
            >
              <span
                className="amostra"
                style={{
                  background: visiveis[b] ? `${CORES[b]}44` : "transparent",
                  border: `1.5px solid ${CORES[b]}`,
                }}
              />
              <span className="balde-rotulo">{ROTULO[b]}</span>
              <span className="balde-n">{mapa.data?.totais_com_geometria[b] ?? "—"}</span>
            </button>
          ))}

          <hr className="divisor" />
          <div className="legenda-titulo">Mapa base</div>
          {/* Botões, e não `select`: a troca de mapa base é comparação — o
              curador alterna entre ruas e satélite olhando o mesmo polígono, e
              um menu suspenso cobre justamente a parte que ele quer ver. */}
          <div className="basemaps" role="group" aria-label="Mapa base">
            {BASEMAPS.map((b) => (
              <button
                key={b.id}
                className="chip"
                aria-pressed={basemap.id === b.id}
                onClick={() => {
                  setBasemapId(b.id);
                  salvarBasemap(b.id);
                }}
              >
                {b.nome}
              </button>
            ))}
          </div>
          <p className="discreto" style={{ marginTop: 6 }}>
            {basemap.atribuicao}
          </p>

          <hr className="divisor" />
          <div className="legenda-titulo">Filtro geográfico</div>
          <div className="campo">
            <label htmlFor="mapa-uf">Unidade federativa</label>
            <select id="mapa-uf" value={uf} onChange={(e) => setUf(e.target.value)}>
              <option value="">Todas</option>
              {(mapa.data?.ufs_disponiveis ?? []).map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5 }}>
            <input
              type="checkbox"
              checked={soAreaVisivel}
              onChange={(e) => setSoAreaVisivel(e.target.checked)}
            />
            Carregar só a área visível
          </label>

          <hr className="divisor" />
          <div className="legenda-titulo">Limite por camada</div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="mono"
              type="number"
              aria-label="Limite por camada"
              min={100}
              step={100}
              defaultValue={limite}
              onBlur={(e) => setLimite(Number(e.target.value) || 1500)}
              style={{
                width: 110,
                background: "var(--sup2)",
                border: "1px solid var(--linha)",
                borderRadius: 7,
                padding: "7px 9px",
                color: "var(--tinta)",
              }}
            />
          </div>
          {/* O limite é POR CAMADA, não por tema. Sem a partição, um tema com
              uma camada volumosa fazia a pequena sumir do mapa — e o curador
              concluía que não havia dado. */}
          <p className="discreto" style={{ marginTop: 6 }}>
            Aplicado a cada camada separadamente.
          </p>

          {mapa.data ? <Diagnostico dados={mapa.data} /> : null}
        </div>
      </div>
    </>
  );
}

/**
 * A comparação que responde "cadê o resto?".
 *
 * `totais_brutos` menos `sem_filtro_espacial` = registros sem geometria
 * nenhuma. Usar o total filtrado nessa conta contaria "fora da área visível"
 * como "sem geometria", que foi um bug real da PoC e inflava o número
 * enormemente.
 */
function Diagnostico({ dados }: { dados: DadosMapa }): ReactNode {
  const semGeometria = BALDES.reduce(
    (n, b) => n + dados.totais_brutos[b] - dados.totais_com_geometria_sem_filtro_espacial[b],
    0,
  );
  const cortado = BALDES.some((b) => dados.cortado[b]);

  return (
    <>
      <hr className="divisor" />
      <div className="legenda-titulo">Diagnóstico</div>
      {cortado ? (
        <p className="discreto">
          Alguma camada bateu o limite de {dados.limite_aplicado} — há mais dado do que está
          desenhado.
        </p>
      ) : null}
      {semGeometria > 0 ? (
        <p className="discreto" style={{ color: "var(--agd)" }}>
          {semGeometria} registro(s) sem geometria aproveitável. O dado bruto foi preservado, mas
          não aparece no mapa.
        </p>
      ) : (
        <p className="discreto">Todo registro ingerido tem geometria.</p>
      )}
      <p className="discreto">
        Geometria simplificada em {dados.tolerancia_simplificacao.toFixed(5)}° para desenhar.
      </p>
    </>
  );
}

function Tela({
  dados,
  visiveis,
  basemap,
  aoMover,
}: {
  dados: DadosMapa | null;
  visiveis: Record<Balde, boolean>;
  basemap: Basemap;
  aoMover: (bbox: string) => void;
}): ReactNode {
  const [coords, setCoords] = useState("lat: — · lon: —");
  const grupos = useRef<Record<Balde, L.LayerGroup> | null>(null);

  // A criação do mapa, a camada base e a troca dela em vida, o `invalidateSize`
  // e a proteção contra recriação por callback vivem no hook compartilhado
  // (`pecas/mapaLeaflet.ts`). Aqui fica só o que é específico desta tela: os
  // três baldes e o desenho.
  const { divRef, mapa, geracao } = useMapaLeaflet({
    aoPassar: (lat, lon) => setCoords(`lat: ${lat.toFixed(5)} · lon: ${lon.toFixed(5)}`),
    aoMover,
    basemap,
  });

  // Os grupos de camada, um por balde, criados quando o mapa nasce. Ligados à
  // `geracao` para reaparecerem se o mapa for recriado.
  useEffect(() => {
    void geracao;
    if (!mapa) return;
    grupos.current = {
      publicado: L.layerGroup().addTo(mapa),
      validado: L.layerGroup().addTo(mapa),
      aguardando_validacao: L.layerGroup().addTo(mapa),
    };
    return () => {
      grupos.current = null;
    };
  }, [mapa, geracao]);

  useEffect(() => {
    // `geracao` não é lida no corpo — entra como dependência para o desenho
    // refazer depois de o mapa ser recriado (troca de tela, remontagem).
    void geracao;
    if (!dados || !grupos.current || !mapa) return;
    let algum = false;
    const envelope = L.latLngBounds([]);

    for (const balde of BALDES) {
      const g = grupos.current[balde];
      g.clearLayers();
      if (!visiveis[balde]) continue;
      for (const f of dados[balde]) {
        const camada = desenharFeicao(g, f.geometria, {
          cor: CORES[balde]!,
          tracejado: balde === "aguardando_validacao",
          enquadrar: false,
        });
        camada.bindPopup(resumo(f));
        const b = camada.getBounds();
        if (b.isValid()) {
          envelope.extend(b);
          algum = true;
        }
      }
    }
    if (algum) mapa.fitBounds(envelope, { padding: [24, 24], maxZoom: 12 });
  }, [dados, visiveis, geracao, mapa]);

  return (
    <>
      {/* Classe CONSTANTE: é o nó do Leaflet, e ele escreve classes próprias
          nele. Qualquer `className` que o React recalcule aqui apaga as dele.
          O tema e `inverte-base` vivem no `.mapa-area`, que é o pai. */}
      <div ref={divRef} className="mapa-tela" />

      <div className="mapa-coords">{coords}</div>
    </>
  );
}

function resumo(f: FeicaoMapa): string {
  const linhas = Object.entries(f.atributos)
    .slice(0, 8)
    .map(([k, v]) => `<b>${escapar(k)}</b>: ${escapar(String(v))}`)
    .join("<br>");
  return (
    `<div style="font-family:monospace;font-size:11.5px">` +
    `<b>${escapar(f.codigo_externo ?? "sem código")}</b>` +
    `${f.uf ? ` · ${escapar(f.uf)}` : ""}<hr>${linhas}</div>`
  );
}

/** Atributo vem de fonte pública; entra em `innerHTML` do popup do Leaflet. */
function escapar(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
